import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { createColumnHelper, RowData } from '@tanstack/react-table'

import { Button, Dialog, DialogContent, DialogTitle, IconButton, Tooltip } from '@mui/material'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'

import Table from '@/components/ui/tables/Table'
import LoadingSwitch from '@/components/ui/switchs/LoadingSwitch'

declare module '@tanstack/react-table' {
  interface TableMeta<TData extends RowData> {
    selectLicensee?: (licenseeId: string, isSelected: boolean) => void
  }
}

type LicenseeData = {
  id: string
  email: string
  name: string
  isAssigned: boolean
  isAssigning?: boolean
  isDisabled?: boolean
}

type SelectLicenseesDialogProps = {
  licensees: LicenseeData[]
  onSelectLicensee?: (licenseeId: string, isSelected: boolean) => void
}

const columnHelper = createColumnHelper<LicenseeData>()

export default function SelectLicenseesDialog(props: SelectLicenseesDialogProps) {
  const { licensees, onSelectLicensee } = props
  const { t } = useTranslation()

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: 'Name',
      }),
      columnHelper.accessor('email', {
        header: 'Email',
      }),
      columnHelper.display({
        id: 'actions',
        cell: ({ row, table }) => (
          <LoadingSwitch
            checked={row.original.isAssigned}
            disabled={row.original.isDisabled}
            loading={row.original.isAssigning}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              table.options.meta?.selectLicensee?.(row.original.id, event.target.checked)
            }}
            size="medium"
          />
        ),
        meta: {
          align: 'right',
          sxCell: { padding: '12px' },
        },
      }),
    ],
    []
  )

  const [open, setOpen] = useState(false)

  const handleClose = () => {
    setOpen(false)
  }

  return (
    <>
      <Button
        onClick={() => {
          setOpen(true)
        }}
        variant="contained"
      >
        {t('dialog.selectLicensees.openButton.label')}
      </Button>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md" scroll="paper">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', flexDirection: 'row', justifyContent: 'space-between' }}>
          {t('dialog.selectLicensees.title')}
          <Tooltip title={t('common:button.close.tooltip')}>
            <IconButton onClick={handleClose}>
              <CloseRoundedIcon />
            </IconButton>
          </Tooltip>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <Table
            columns={columns}
            data={licensees}
            meta={{
              selectLicensee: onSelectLicensee,
            }}
            selectable={false}
          />
        </DialogContent>
      </Dialog>
    </>
  )
}
